import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useNavigationType } from 'react-router-dom';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import styled from 'styled-components';

import themeColors from 'theme/colors.json';
import { getToken } from 'utils/localStorage';
import { PageLoading } from 'components/loading';
import {
  INBOUND_TYPES,
  OUTBOUND_TYPES,
  UrlParams,
} from 'utils/formHost/bridgeMessages';
import {
  parseUrlParams,
  computeIframeSrc,
  resolveSubmitSuccessRoute,
  parseBridgeMessage,
  buildInitMessage,
} from 'utils/formHost/formHost.helpers';
import { useAccessToken } from './useAccessToken';
import FlowErrorToast from './FlowErrorToast';

/**
 * URL of the deployed forms-host. Falls back to the local Vite dev server.
 * Production: set FORMS_HOST_URL in the matching env file under client_config/.
 * webpack's DefinePlugin inlines every key from that file as process.env.<KEY>.
 */
const FORMS_HOST_URL = process.env.FORMS_HOST_URL || 'http://localhost:5173';

/**
 * Bare origin (`scheme://host:port`) of the forms-host, used for the inbound
 * `event.origin` check and the `postMessage` targetOrigin. `event.origin` is
 * always a bare origin, so comparing against the full URL would silently break
 * the bridge if FORMS_HOST_URL ever carried a trailing slash or path. Falls
 * back to the raw value if the URL is malformed.
 */
const FORMS_HOST_ORIGIN = (() => {
  try {
    return new URL(FORMS_HOST_URL).origin;
  } catch {
    return FORMS_HOST_URL;
  }
})();

/** Post-submit navigation destinations keyed by flowId. */
const SUBMIT_SUCCESS_ROUTES: Record<string, string> = {
  // Add real flow destinations here as they are migrated, e.g.:
  // 'external-bank': '/work/payments',
};

/** Fallback destination for flows not listed in SUBMIT_SUCCESS_ROUTES. */
const SUBMIT_SUCCESS_FALLBACK = '/';

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const StyledIframe = styled.iframe`
  border: none;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const LoadingWrapper = styled.div<{ ready: boolean }>`
  position: absolute;
  inset: 0;
  background: #ffffff;
  z-index: 1;
  opacity: ${({ ready }) => (ready ? 0 : 1)};
  pointer-events: ${({ ready }) => (ready ? 'none' : 'auto')};
  transition: opacity 200ms ease;
`;

/**
 * Embeds the forms-host inside backoffice. Responsibilities:
 *   1. Compute the iframe URL from route params (`/flow/:flowId/*`).
 *   2. Send `init` once the iframe loads (and again on `ready`).
 *   3. Forward token refreshes via `token:update`.
 *   4. Handle outbound bridge messages (navigation, submit, cancel, …).
 */
const FormHost = (): ReactElement => {
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const params = useParams();
  const flowId = params.flowId;
  const subPath = params['*'] || '';

  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Path the iframe last navigated to. Distinguishes a host-driven navigation
  // (menu click / deep-link) from the iframe mirroring its own route.
  const iframePathRef = useRef(subPath);

  const [ready, setReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const token = useAccessToken();

  // Parsed once per mount; flows are not expected to react to URL changes mid-session.
  const urlParams = useMemo<UrlParams>(
    () => parseUrlParams(window.location.search, window.location.hash),
    [],
  );

  const buildInit = useCallback(
    () =>
      buildInitMessage({
        token: urlParams.jwt || getToken(),
        tenant: process.env.CUSTOMER_ID,
        locale: i18n.locale || 'en',
        theme: themeColors as Record<string, unknown>,
        flowId,
        subPath,
        urlParams,
      }),
    [flowId, subPath, urlParams],
  );

  const postInit = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(JSON.stringify(buildInit()), FORMS_HOST_ORIGIN);
  }, [buildInit]);

  // Send `init` after the iframe finishes loading.
  const handleLoad = useCallback(() => postInit(), [postInit]);

  // Safety net: drop the overlay after 5s if `flow:ready` never arrives.
  useEffect(() => {
    if (ready) return undefined;
    const id = setTimeout(() => setReady(true), 5000);
    return () => clearTimeout(id);
  }, [ready]);

  // Forward token refreshes to the iframe once it is ready.
  useEffect(() => {
    if (!ready || !token) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(
      JSON.stringify({ type: INBOUND_TYPES.TOKEN_UPDATE, token }),
      FORMS_HOST_ORIGIN,
    );
  }, [token, ready]);

  // Listen for outbound messages from the iframe. Origin must match.
  useEffect(() => {
    const handler = (event: MessageEvent): void => {
      if (event.origin !== FORMS_HOST_ORIGIN) return;
      const msg = parseBridgeMessage(event.data);
      if (!msg) return;

      switch (msg.type) {
        case OUTBOUND_TYPES.READY:
          // Re-post init (idempotent) to cover the listener-attachment race.
          postInit();
          break;

        case OUTBOUND_TYPES.FLOW_READY:
          setReady(true);
          break;

        case OUTBOUND_TYPES.ROUTE_CHANGE: {
          if (typeof msg.path === 'string') {
            const relPath = msg.path.replace(/^\/+/, '');
            iframePathRef.current = relPath;
            if (relPath !== subPath) {
              const prefix = `/flow/${flowId}`;
              navigate(relPath ? `${prefix}/${relPath}` : prefix, { replace: true });
            }
          }
          break;
        }

        case OUTBOUND_TYPES.SUBMIT_SUCCESS:
          navigate(
            resolveSubmitSuccessRoute(
              typeof msg.flowId === 'string' ? msg.flowId : flowId,
              SUBMIT_SUCCESS_ROUTES,
              SUBMIT_SUCCESS_FALLBACK,
            ),
          );
          break;

        case OUTBOUND_TYPES.SUBMIT_ERROR:
          setErrorMessage(
            typeof msg.error === 'string'
              ? msg.error
              : t`There was an error submitting the form. Please try again.`,
          );
          break;

        case OUTBOUND_TYPES.CANCEL:
          navigate(-1);
          break;

        case OUTBOUND_TYPES.NAVIGATE_NATIVE: {
          if (typeof msg.route === 'string') {
            const isAbsolute = /^https?:\/\//i.test(msg.route);
            if (isAbsolute) {
              const navParams = msg.params as { sameApp?: boolean } | undefined;
              if (navParams && navParams.sameApp === false) {
                window.open(msg.route, '_blank', 'noopener,noreferrer');
              } else {
                window.location.href = msg.route;
              }
            } else {
              navigate(msg.route, { state: msg.params });
            }
          }
          break;
        }

        case OUTBOUND_TYPES.TELEMETRY:
        default:
          break;
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [navigate, postInit, flowId, subPath]);

  // `src` is pinned per flow so in-flow navigation (iframe REPLACE / POP) does
  // not reload the iframe. Reload only on a flow switch or a host-driven PUSH
  // to a new path (menu / deep-link) — navigation the iframe can't perform.
  const srcRef = useRef<{ flowId?: string; src: string }>({ flowId: undefined, src: '' });
  const hostPushedNewPath = navigationType === 'PUSH' && subPath !== iframePathRef.current;
  if (srcRef.current.flowId !== flowId || hostPushedNewPath) {
    iframePathRef.current = subPath;
    srcRef.current = { flowId, src: computeIframeSrc(FORMS_HOST_URL, flowId || '', subPath) };
  }
  const src = srcRef.current.src;

  return (
    <Container>
      <StyledIframe
        ref={iframeRef}
        src={src}
        title="Toqio flow"
        onLoad={handleLoad}
        referrerPolicy="strict-origin"
        // forms-host is always served from a DISTINCT origin (FORMS_HOST_URL),
        // so allow-scripts + allow-same-origin do not let it script this parent;
        // same-origin is required for the framed app's own storage/cookies.
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        allow="clipboard-write"
      />
      <FlowErrorToast message={errorMessage} onDismiss={() => setErrorMessage(null)} />
      <LoadingWrapper ready={ready} aria-hidden={ready}>
        <PageLoading />
      </LoadingWrapper>
    </Container>
  );
};

export default FormHost;
